import Lottie from 'lottie-react';
import { memo } from 'react';

import { cn } from '@air/utils-tailwind';

import ALineSlowOptDark from '~/components/v2/components/workspace/workspace-agent-panel/animations/ALineSlowOptDark.json';
import ALineSlowOptLight from '~/components/v2/components/workspace/workspace-agent-panel/animations/ALineSlowOptLight.json';
import styles from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/workspaceAgentSpaceFabMark.module.css';
import {
  getFabGradientTransitionStyle,
  getFabLabelTransitionStyle,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/workspaceAgentSpaceMotion';

export type WorkspaceAgentSpaceFabMarkProps = {
  isExpanded: boolean;
  isDarkTheme: boolean;
  shouldReduceMotion?: boolean;
};

/**
 * Two radial gradients for a lit top-left and shaded bottom-right, over an altitude sweep. The
 * colour variables are theme-aware, so this inverts with the rest of the FAB.
 */
const GRADIENT_STYLE = (shouldReduceMotion: boolean) => ({
  backgroundImage: [
    'radial-gradient(ellipse 78% 58% at 20% 14%, rgb(255 255 255 / 0.11) 0%, transparent 52%)',
    'radial-gradient(ellipse 110% 95% at 92% 96%, rgb(0 0 0 / 0.38) 0%, transparent 62%)',
    'linear-gradient(148deg, var(--colors-altitude-9) 0%, var(--colors-altitude-12) 52%, var(--colors-altitude-9) 100%)',
  ].join(', '),
  backgroundSize: '220% 220%',
  backgroundPosition: '0% 50%',
  boxShadow: 'inset 0 1px 0 0 rgb(255 255 255 / 0.09), inset 0 -1px 0 0 rgb(0 0 0 / 0.16)',
  transitionProperty: 'opacity',
  ...getFabGradientTransitionStyle(shouldReduceMotion),
});

/**
 * The expanded pill's gradient is a dark blue in both themes, so it always needs the light mark —
 * the dark one would sit at roughly 1.5:1 against it.
 */
const getFabAnimationData = ({
  isDarkTheme,
  isExpanded,
}: Pick<WorkspaceAgentSpaceFabMarkProps, 'isDarkTheme' | 'isExpanded'>) => {
  if (isExpanded) {
    return ALineSlowOptLight;
  }

  return isDarkTheme ? ALineSlowOptDark : ALineSlowOptLight;
};

export const WorkspaceAgentSpaceFabMark = memo(
  ({ isExpanded, isDarkTheme, shouldReduceMotion = false }: WorkspaceAgentSpaceFabMarkProps) => {
    const labelTransitionStyle = getFabLabelTransitionStyle(shouldReduceMotion);

    return (
      <>
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0 overflow-hidden rounded-full opacity-0 ease-[cubic-bezier(0.16,1,0.3,1)]',
            isExpanded && 'opacity-100',
            isExpanded && !shouldReduceMotion && styles.gradientDrift,
          )}
          style={GRADIENT_STYLE(shouldReduceMotion)}
        />
        <Lottie
          animationData={getFabAnimationData({ isDarkTheme, isExpanded })}
          aria-hidden
          className="relative size-6 shrink-0"
          loop={!shouldReduceMotion}
        />
        <span
          className={cn(
            'relative ml-0 max-w-0 overflow-hidden whitespace-nowrap font-medium text-14 opacity-0',
            isExpanded && 'ml-1 max-w-24 opacity-100',
          )}
          style={labelTransitionStyle}
        >
          Start task
        </span>
      </>
    );
  },
);

WorkspaceAgentSpaceFabMark.displayName = 'WorkspaceAgentSpaceFabMark';
